/* ==========================================================================
   DIREVULF — delivery.js

   "What does delivery cost to here", asked once and answered by Shopify.

   The widget lives in snippets/delivery-check.liquid and appears on the bag
   and on the product page. This file drives both, so there is exactly one
   implementation and the two surfaces can never quote different numbers.

   WHAT IT DOES AND DOES NOT DO
   ----------------------------
   It calls Shopify's own /cart/shipping_rates.json for a real destination and
   reports what comes back. It never estimates a rate, never assumes a zone and
   never invents a free-shipping threshold. Delivery timing is shown only when
   Shopify itself sends it on the rate (delivery_date, or delivery_days) — the
   theme neither calculates a window nor guesses one, so a store with no
   carrier timing simply shows the price. If Shopify offers nothing for that
   address, it says so.

   The rate is announced on the document as `dv:delivery:quote`. The bag listens
   for it and stores the figure on the cart so Liquid — not this file — renders
   the money. Nothing here formats an amount.
   ========================================================================== */
(function () {
  'use strict';

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  var S = {};
  function readStrings() {
    var node = $('[data-dv-delivery-strings]');
    if (!node) return {};
    try { return JSON.parse(node.textContent); } catch (e) { return {}; }
  }

  function fill(template, vars) {
    var out = template || '';
    Object.keys(vars || {}).forEach(function (k) {
      out = out.split('%' + k.toUpperCase() + '%').join(vars[k]);
    });
    return out;
  }

  function say(box, message, tone) {
    var note = $('[data-dv-deliver-note]', box);
    if (!note) return;
    note.textContent = message || '';
    note.hidden = !message;
    note.classList.toggle('is-error', tone === 'error');
  }

  /* ------------------------------------------------------------- provinces */
  /* Shopify's own country data, carried on each option of country_option_tags
     as data-provinces. No list of states is kept in this theme. */
  function provincesFor(select) {
    var option = select.options[select.selectedIndex];
    if (!option) return [];
    try { return JSON.parse(option.getAttribute('data-provinces') || '[]'); }
    catch (e) { return []; }
  }

  /* country_option_tags carries a "---" divider between the shop's popular
     countries and the full list. It is not a place, so it must not be a
     choice, and it must not be what a shopper is left on by default. */
  function selectCountry(box) {
    var select = $('[data-dv-deliver-country]', box);
    if (!select) return;

    var divider = null;
    var match = null;
    var wanted = (box.getAttribute('data-country') || '').toLowerCase();

    $$('option', select).forEach(function (option) {
      if (option.value === '---') { divider = option; return; }
      if (wanted && option.value.toLowerCase() === wanted) match = option;
    });
    if (divider) divider.disabled = true;

    if (match) { select.value = match.value; return; }
    /* No country to go on: start on a real one rather than the divider. */
    if (select.selectedIndex === -1 || select.value === '---') {
      var real = $$('option', select).filter(function (option) {
        return option.value !== '---';
      })[0];
      if (real) select.value = real.value;
    }
  }

  function paintProvinces(box) {
    var country = $('[data-dv-deliver-country]', box);
    var province = $('[data-dv-deliver-province]', box);
    if (!country || !province) return;

    var list = provincesFor(country);
    var wanted = box.getAttribute('data-province') || '';
    province.innerHTML = '';

    if (!list.length) {
      /* A country Shopify holds no provinces for needs no state field. */
      province.hidden = true;
      return;
    }
    var matched = false;
    list.forEach(function (pair) {
      var option = document.createElement('option');
      option.value = pair[0];
      option.textContent = pair[1];
      if (pair[0] === wanted || pair[1] === wanted) { option.selected = true; matched = true; }
      province.appendChild(option);
    });
    /* With no state to go on, the field starts empty instead of on the first
       state in the list. Left there, a shopper who only types a pincode is
       quoted for that first state and told their pincode is wrong for it;
       empty, Shopify answers "Select a state", which they can act on. */
    if (!matched) {
      var none = document.createElement('option');
      none.value = '';
      none.textContent = S.pincode_state || '';
      none.disabled = true;
      none.selected = true;
      province.insertBefore(none, province.firstChild);
    }
    province.hidden = false;
  }

  /* Shopify's own delivery window for the rate it just quoted, or nothing.
     Never calculated, never rounded up, never invented. */
  function timing(rate) {
    if (!rate) return '';
    if (rate.date) return rate.date;
    var days = rate.days;
    if (!days || !days.length) return '';
    var min = days[0];
    var max = days.length > 1 ? days[days.length - 1] : days[0];
    return fill(S.pincode_days, { min: min, max: max });
  }

  /* What to tell the shopper when Shopify refuses the address. A box that
     asked for a state repeats Shopify's own words, because they name the field
     the shopper can fix. A pincode-only box has no such field, so repeating
     them would be an instruction it cannot carry out; it says plainly that the
     pincode cannot be confirmed instead. */
  function message_for(box, error) {
    var compact = box.getAttribute('data-compact') === 'true';
    var field = (error && error.field) || '';
    if (compact && (field === 'province' || field === 'city' || field === 'address1')) {
      return S.pincode_region_unknown || S.pincode_error || '';
    }
    if (error && error.shopify) return error.shopify;
    return S.pincode_error || '';
  }

  /* ---------------------------------------------------------------- quoting */
  function quote(pin, country, province) {
    var url = ROOT + 'cart/shipping_rates.json'
      + '?shipping_address%5Bzip%5D=' + encodeURIComponent(pin)
      + '&shipping_address%5Bcountry%5D=' + encodeURIComponent(country || '')
      + '&shipping_address%5Bprovince%5D=' + encodeURIComponent(province || '');

    return fetch(url, { headers: { 'Accept': 'application/json' } })
      .then(function (response) {
        return response.json().then(function (payload) {
          if (!response.ok) {
            /* Shopify says exactly what it is missing or refusing. Its own
               words are more useful than anything this file could invent. */
            var first = payload && Object.keys(payload)[0];
            var detail = first && payload[first] && payload[first][0];
            var error = new Error(detail || 'shipping-rates-failed');
            error.shopify = detail || '';
            /* Which part of the address Shopify is missing. The compact box
               never asked for a state, so "Select a state" is not something
               its shopper can act on and must not be repeated at them. */
            error.field = first || '';
            /* Shopify answered and refused. That is a real answer about this
               address, unlike a connection that never got through. */
            error.answered = true;
            throw error;
          }
          return payload;
        });
      })
      .then(function (payload) {
        var rates = (payload && payload.shipping_rates) || [];
        if (!rates.length) return null;
        /* Shopify may offer several. The cheapest is what a shopper is quoted
           by default, and it is the one that keeps a total honest rather than
           flattering. */
        var best = rates.slice().sort(function (a, b) {
          return parseFloat(a.price) - parseFloat(b.price);
        })[0];
        var minor = Math.round(parseFloat(best.price) * 100);
        /* delivery_days and delivery_date are Shopify's own, carried on the
           rate it just quoted. They are the only delivery timing this theme
           will ever show, and they are absent unless Shopify sent them. */
        return {
          minor: isNaN(minor) ? 0 : minor,
          name: best.name || '',
          price: best.price,
          days: best.delivery_days || null,
          date: best.delivery_date || null
        };
      });
  }

  /* A pincode-only box carries a country and a state the shopper never typed —
     from the cart, or from their account — so the pincode they type now can
     disagree with them, and Shopify rejects the pair. The honest response is to
     ask again without the state and let a country-level shipping zone answer if
     the store has one. If that fails too, the pincode simply cannot be
     confirmed, and saying which state was tried would name one they never gave. */
  function quoteFor(box, where) {
    var compact = box.getAttribute('data-compact') === 'true';
    return quote(where.pin, where.country, where.province).catch(function (error) {
      if (!compact || !where.province || !error || !error.answered) throw error;
      return quote(where.pin, where.country, '').catch(function (second) {
        var unconfirmed = new Error('pincode-unconfirmed');
        unconfirmed.field = 'province';
        unconfirmed.shopify = '';
        unconfirmed.answered = !!(second && second.answered);
        throw unconfirmed;
      });
    });
  }

  function readWhere(box) {
    var country = $('[data-dv-deliver-country]', box);
    var province = $('[data-dv-deliver-province]', box);
    var pin = $('input[name="pincode"]', box);
    /* The divider is not a country, and an unmounted select can still be
       sitting on it. Either way the address the cart already carries is the
       better answer than a dash Shopify would reject. */
    var chosen = country ? country.value : '';
    if (!chosen || chosen === '---') chosen = box.getAttribute('data-country') || '';
    return {
      pin: pin ? pin.value.trim() : (box.getAttribute('data-pin') || ''),
      country: chosen,
      province: province && !province.hidden && province.value
        ? province.value
        : (box.getAttribute('data-province') || '')
    };
  }

  /* Announced rather than acted on, so each surface decides what a quote means
     for it: the bag stores it on the cart, the product page just shows it. */
  function announce(where, rate) {
    document.dispatchEvent(new CustomEvent('dv:delivery:quote', {
      detail: { pin: where.pin, country: where.country, province: where.province, rate: rate }
    }));
  }

  function check(box, silent) {
    /* A check can be asked for before the widget this page just re-rendered
       has been set up — the bag re-quotes as soon as the cart changes. */
    hydrate(box);
    var where = readWhere(box);
    if (!where.pin) {
      if (!silent) say(box, S.pincode_invalid || '', 'error');
      return Promise.resolve(null);
    }
    /* data-pin/country/province describe what the CART holds, not what is
       being asked for. The bag compares an incoming quote against them to
       decide whether anything actually changed, so stamping the new address
       here would hide the change from it. Liquid refreshes them when the
       cart is written and the section re-renders. */
    box.setAttribute('aria-busy', 'true');

    return quoteFor(box, where)
      .then(function (rate) {
        if (!rate) { say(box, S.pincode_none || ''); announce(where, null); return null; }
        var line = rate.minor === 0
          ? fill(S.pincode_quoted_free, { pin: where.pin })
          : fill(S.pincode_quoted, { name: rate.name, pin: where.pin });
        var when = timing(rate);
        say(box, when ? line + ' · ' + when : line);
        announce(where, rate);
        return rate;
      })
      .catch(function (error) {
        say(box, message_for(box, error), 'error');
        /* Shopify refusing this address means the figure stored for the old
           one no longer applies, so it is withdrawn rather than left standing
           against an address it was never quoted for. A request that simply
           never got through says nothing about the address, so the stored
           figure survives it and the shopper is told to try again. */
        if (error && error.answered) announce(where, null);
        return null;
      })
      .then(function (rate) {
        box.removeAttribute('aria-busy');
        return rate;
      });
  }

  window.DirevulfDelivery = { check: check, quote: quote };

  /* ---------------------------------------------------------------- wiring */
  document.addEventListener('change', function (event) {
    var country = event.target.closest('[data-dv-deliver-country]');
    if (!country) return;
    var box = country.closest('[data-dv-deliver]');
    if (!box) return;
    box.setAttribute('data-province', '');
    paintProvinces(box);
  });

  document.addEventListener('submit', function (event) {
    var form = event.target.closest('[data-dv-deliver-form]');
    if (!form) return;
    event.preventDefault();
    var box = form.closest('[data-dv-deliver]');
    if (box) check(box, false);
  });

  /* The bag re-renders itself through the Section Rendering API after every
     change, which replaces this widget with a fresh, unwired copy. So mounting
     is idempotent and runs again whenever a new widget appears, rather than
     once at load. The same applies to the product page in due course. */

  /* Setting the fields up is separate from asking for a quote, because a
     check may itself have to hydrate the widget first and must not end up
     firing a second request through it. */
  function hydrate(box) {
    if (box.dvDeliverHydrated) return;
    box.dvDeliverHydrated = true;
    selectCountry(box);
    paintProvinces(box);
  }

  function mount(box) {
    if (box.dvDeliverMounted) return;
    box.dvDeliverMounted = true;
    hydrate(box);

    /* A rate already stored on the cart is the answer; asking Shopify again
       for it would change nothing. Only an address with no answer yet is
       worth a request — which is how a signed-in shopper with an address on
       file gets a figure without typing anything. */
    var pin = (box.getAttribute('data-pin') || '').trim();
    var rate = (box.getAttribute('data-rate') || '').trim();
    var asked = box.getAttribute('data-asked') === 'true';
    /* An address that came from the shopper's account is a suggestion worth
       pricing. One the cart has already been asked about is not: if it came
       back with nothing, asking again on every re-render would just repeat
       the same empty answer. */
    if (pin && !rate && !asked) check(box, true);
  }

  function mountAll() {
    S = readStrings();
    $$('[data-dv-deliver]').forEach(mount);
  }

  var pending = false;
  function scheduleMount() {
    if (pending) return;
    pending = true;
    requestAnimationFrame(function () { pending = false; mountAll(); });
  }

  function start() {
    mountAll();
    if (!window.MutationObserver || !document.body) return;
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        if (records[i].addedNodes.length) { scheduleMount(); return; }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();